import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";

import { getSticker } from "../api/ipdEnhancementsApi";
import { payerTypeLabel, payerDetailLabel } from "../utils/payerLabels";

// When printing, hide everything except the sticker area. The area is rendered
// inside the MUI Dialog portal (at <body>), so a global print rule scoped to
// the sticker id is the simplest reliable way to print only the wristband.
const PRINT_STYLES = `
@media print {
  body * { visibility: hidden !important; }
  #ipd-sticker-print-area, #ipd-sticker-print-area * { visibility: visible !important; }
  #ipd-sticker-print-area {
    position: fixed; left: 0; top: 0; width: 100%;
    padding: 16px; box-sizing: border-box;
  }
  .ipd-sticker-no-print { display: none !important; }
}
`;

function Line({ label, value }) {
  if (!value) return null;
  return (
    <Typography sx={{ fontSize: "13px", mt: 0.4 }}>
      <strong>{label}:</strong> {value}
    </Typography>
  );
}

export default function StickerPrintModal({ open, onClose, admissionId }) {
  const [loading, setLoading] = useState(false);
  const [sticker, setSticker] = useState(null);

  useEffect(() => {
    if (!open || !admissionId) return undefined;
    let active = true;
    setLoading(true);
    setSticker(null);
    getSticker(admissionId)
      .then((data) => {
        if (active) setSticker(data);
      })
      .catch((err) => {
        console.error("Failed to load sticker:", err);
        toast.error(
          err?.response?.data?.message || "Failed to load patient sticker."
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, admissionId]);

  const patient = sticker?.patient || {};
  const bed = sticker?.bed || {};
  const doctor = sticker?.admitting_doctor || {};
  const secondaryDoctors = sticker?.secondary_doctors || [];
  // Self Pay / Staff have no payer name, so payerDetailLabel returns null and
  // the detail row is skipped entirely.
  const payerDetailHeading = payerDetailLabel(sticker?.payer_type);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <style>{PRINT_STYLES}</style>
      <DialogTitle sx={{ fontWeight: 700 }}>Patient Sticker</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !sticker ? (
          <Typography sx={{ color: "#777" }}>No sticker data available.</Typography>
        ) : (
          <Box
            id="ipd-sticker-print-area"
            sx={{
              border: "1px solid #ccc",
              borderRadius: 1,
              p: 2,
              display: "flex",
              gap: 2,
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
                {patient.name || "—"}
              </Typography>
              <Line label="UHID" value={patient.uhid} />
              <Line label="Patient ID" value={patient.patient_id} />
              <Line label="Gender" value={patient.gender} />
              <Line label="Blood Group" value={patient.blood_group} />
              <Divider sx={{ my: 0.8 }} />
              <Line label="Bed" value={bed.code} />
              <Line label="Ward" value={bed.bed_type_name || bed.location} />
              <Line label="Doctor" value={doctor.name} />
              {secondaryDoctors.length > 0 ? (
                <Line
                  label="Referral Doctors"
                  value={secondaryDoctors
                    .map((d) => d.name)
                    .filter(Boolean)
                    .join(", ")}
                />
              ) : null}
              <Line
                label="Admission"
                value={sticker.admission_id ? `IP-${sticker.admission_id}` : null}
              />
              <Line label="Patient Type" value={payerTypeLabel(sticker.payer_type)} />
              {payerDetailHeading ? (
                <Line label={payerDetailHeading} value={sticker.payer_detail} />
              ) : null}
            </Box>
            {patient.uhid ? (
              <Box sx={{ textAlign: "center" }}>
                <QRCodeSVG value={patient.uhid} size={110} />
              </Box>
            ) : null}
          </Box>
        )}
      </DialogContent>
      <DialogActions className="ipd-sticker-no-print">
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={() => window.print()}
          disabled={loading || !sticker}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
}
